'use client'

import { useState, useSyncExternalStore } from 'react'
import Link from 'next/link'
import { FaLinkedinIn, FaYoutube } from 'react-icons/fa6'
import BrandLogo from '@/components/shell/BrandLogo'
import FooterArt from '@/components/shell/FooterArt'
import { Chevron } from '@/components/shell/primitives'
import { YOUTUBE_CHANNEL_URL } from '@/lib/seo/brand-constants'
import { getVersion } from '@/lib/version'
import { reopenConsentBanner } from '@/lib/consent-manager'

/**
 * Site footer in the reference footer's markup and classes
 * (public/pixelmojo-rebuild, `footer.sheet.sheet--ink.footer`), carrying the
 * site's full link set: Products, Quick Links, Services and Get in Touch,
 * social links, legal links and cookie settings. The newsletter signup moved
 * to the home page's closing section on 2026-10-09 (Lloyd;
 * src/components/reference/HomeNewsletterForm.tsx). On phones the Products, Quick Links and Services columns are an
 * accordion (one open at a time, as in the header's mobile menu); every link
 * stays in the server HTML, which always renders the open desktop columns.
 */
type FooterLink = { label: string; href: string }

const PRODUCTS: FooterLink[] = [
  { label: 'All Products', href: '/products' },
  { label: 'Vector', href: '/vector' },
  { label: 'Hive', href: '/hive' },
  { label: 'Radar', href: '/platform' },
  { label: 'Radar Sites', href: '/radar-sites' },
  { label: 'Radar Sample Report', href: '/platform/sample-report' },
  { label: 'Brand System', href: '/brand' },
]

const QUICK_LINKS: FooterLink[] = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Projects', href: '/projects' },
  { label: 'About', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blogs' },
  { label: 'Videos', href: '/videos' },
  { label: 'Answers', href: '/answers' },
  { label: 'Tools', href: '/tools' },
  { label: 'Labs', href: 'https://labs.pixelmojo.io' },
  { label: 'Press', href: '/press' },
  { label: 'Get in Touch', href: '/contact-us' },
]

const SERVICES: FooterLink[] = [
  { label: 'Capabilities', href: '/capabilities' },
  { label: 'Consulting', href: '/consulting' },
  { label: 'AI Visibility Strategy', href: '/services/ai-visibility-strategy' },
  { label: 'AI Product Development', href: '/services/ai-product-development' },
  { label: 'Brand & Sales Design', href: '/services/revenue-first-design' },
  { label: 'Growth Marketing', href: '/services/ai-powered-growth' },
  { label: 'Philippines Studio', href: '/philippines' },
]

const LEGAL_LINKS: FooterLink[] = [
  { label: 'Privacy', href: '/privacy-policy' },
  { label: 'Terms', href: '/terms-of-service' },
  { label: 'Refunds', href: '/refund-policy' },
  { label: 'Cookies', href: '/cookie-policy' },
  { label: 'AI Policy', href: '/ai-policy' },
]

const BUILT_WITH = [
  'Next.js',
  'TypeScript',
  'Tailwind',
  'Vercel',
  'Supabase',
  'AWS',
  'OpenAI',
  'Claude',
  'LangChain',
]

const SOCIAL = [
  {
    icon: FaLinkedinIn,
    href: 'https://www.linkedin.com/company/pixelmojo',
    label: 'LinkedIn',
  },
  { icon: FaYoutube, href: YOUTUBE_CHANNEL_URL, label: 'YouTube' },
]

function FooterLinkItem({ link }: { link: FooterLink }) {
  return (
    <li>
      {link.href.startsWith('http') ? (
        <a href={link.href} target='_blank' rel='noopener noreferrer'>
          {link.label}
        </a>
      ) : (
        <Link href={link.href}>{link.label}</Link>
      )}
    </li>
  )
}

// Phones get the accordion. The server snapshot is "not a phone", so the
// HTML (and its first hydration) is always the open desktop columns.
const PHONE = '(max-width: 640px)'
const subscribePhone = (onChange: () => void) => {
  const query = window.matchMedia(PHONE)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
const isPhone = () => window.matchMedia(PHONE).matches
const notPhone = () => false

function Column({
  title,
  links,
  accordion,
  open,
  onToggle,
}: {
  title: string
  links: FooterLink[]
  accordion: boolean
  open: boolean
  onToggle: () => void
}) {
  const listId = `footer-${title.toLowerCase().replace(/\s+/g, '-')}`
  return (
    <div>
      {accordion ? (
        <h3>
          <button
            type='button'
            className='footer__toggle'
            aria-expanded={open}
            aria-controls={listId}
            onClick={onToggle}
          >
            {title}
            <Chevron />
          </button>
        </h3>
      ) : (
        <h3>{title}</h3>
      )}
      <ul id={listId} hidden={accordion && !open}>
        {links.map(link => (
          <FooterLinkItem key={link.href} link={link} />
        ))}
      </ul>
    </div>
  )
}

export default function Footer() {
  const accordion = useSyncExternalStore(subscribePhone, isPhone, notPhone)
  const [openColumn, setOpenColumn] = useState<string | null>(null)
  const column = (title: string, links: FooterLink[]) => (
    <Column
      title={title}
      links={links}
      accordion={accordion}
      open={openColumn === title}
      onToggle={() => setOpenColumn(open => (open === title ? null : title))}
    />
  )

  return (
    <div className='shell contents'>
      <footer className='sheet sheet--ink footer'>
        <div className='container'>
          <div className='footer__top'>
            <div>
              <Link
                className='brand brand--lg'
                href='/'
                aria-label='Pixelmojo home'
              >
                <BrandLogo />
              </Link>
              <p className='footer__tag' style={{ marginTop: 28 }}>
                AI products you own. <em>Intelligence we maintain.</em>
              </p>
              <div className='footer__contact'>
                <span>
                  Pixelmojo · AI product studio · Founded in 2024 by Lloyd
                  Pilapil · Makati, Philippines
                </span>
              </div>
              <div className='footer__social'>
                {SOCIAL.map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={label}
                  >
                    <Icon aria-hidden='true' />
                  </a>
                ))}
              </div>
            </div>
            {column('Products', PRODUCTS)}
            {column('Quick Links', QUICK_LINKS)}
            {column('Services', SERVICES)}
            <div>
              <h3>Get in Touch</h3>
              <ul>
                <li>
                  <a href='mailto:founders@pixelmojo.io'>
                    founders@pixelmojo.io
                  </a>
                </li>
                <li>
                  <span>111 Paseo de Roxas, Legazpi Village</span>
                </li>
                <li>
                  <span>Makati, 1229 Metro Manila</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <FooterArt />
        <div className='footer__base'>
          <div className='footer__bottom'>
            <span>
              © 2026 Pixelmojo · Makati · <span>{getVersion()}</span>
            </span>
            <ul>
              {LEGAL_LINKS.map(link => (
                <FooterLinkItem key={link.href} link={link} />
              ))}
              <li>
                <button type='button' onClick={reopenConsentBanner}>
                  Cookie settings
                </button>
              </li>
            </ul>
          </div>
          <div className='footer__built'>
            <span>Built with</span>
            <ul>
              {BUILT_WITH.map(tool => (
                <li key={tool}>{tool}</li>
              ))}
            </ul>
          </div>
        </div>
      </footer>
    </div>
  )
}
